<template>
	<NavSidebarLayout
		:scrollable="false"
	>
		<PageCrumbHeaderSkeleton
			v-if="showTitleSkeleton"
		/>
		<TeamPageCrumbHeader
			v-else
			class="team-layout__page-crumb-header"
			:crumbs="breadcrumbs"
			@back="onBack"
			@crumb="onCrumb"
		>
			<template #body>
				<div class="team-layout__title-layout">
					<ToggleStar
						v-if="teamName"
						class-name="team-layout__fave-star"
						:is-toggled="!!team.isFavorited"
						@change="onStarClicked"
					/>
					<div class="team-layout__team-name">{{ teamName }}</div>
					<div class="team-layout__team-member-avatars">
						<UserAvatarSpread
							@moreUsersClicked="onNavItemClick( `settings` )"
							more-users-clickable
							:users="team?.members"
							:max-avatars="5"
						/>
					</div>
					<div>
						<AddButton
							size="TINY"
							type="TERTIARY"
							@click="onNavItemClick( `settings` )"
						/>
					</div>
				</div>
			</template>
		</TeamPageCrumbHeader>

		<div class="team-layout__tab-nav-row">
			<TabSet :tabs="tabs" @select="onNavItemClick" />
		</div>

		<slot name="view" :team="team" :team-loaded="teamLoaded">
			<main class="team-layout__tab-container">
				<router-view
					v-if="teamLoaded"
					:team="team"
					:key="teamId"
					v-bind="$attrs"
				/>
			</main>
		</slot>
	</NavSidebarLayout>
</template>

<script>
import compact from "lodash/compact";
import ScreenSizeDetector from "screen-size-detector";
import {
	mapActions,
	mapGetters,
	mapMutations
} from "vuex";

import {
	AddButton,
	DocumentMixin,
	TabSet,
	ToggleStar,
	PageCrumbHeaderSkeleton,
	TranslateMixin,
	UserAvatarSpread,
	makeCrumb
} from "@teaming/common-ui";

import {
	TeamPageCrumbHeader
} from "@teaming/common-team";

export default {
	name: "TeamLayout",
	teamingPackage: "slice-team.layouts",
	mixins: [ TranslateMixin, DocumentMixin, ],
	components: {
		AddButton,
		TabSet,
		PageCrumbHeaderSkeleton,
		ToggleStar,
		TeamPageCrumbHeader,
		UserAvatarSpread,
	},
	inject: [ "userProfile", ],
	data() {
		return {
			screenWidth: window.innerWidth,
			teamLoaded: false,
		};
	},
	props: {
		teamId: {
			type: String,
			required: true,
		},
	},
	watch: {
		teamId( newTeamId, oldTeamId ) {
			if ( newTeamId && newTeamId !== oldTeamId ) {
				this.teamLoaded = false;
				this.applyTeamId( newTeamId );
				this.fetchThisTeam();
			}
		},
		team( newTeam, oldTeam ) {
			if ( newTeam && newTeam.id && newTeam.id !== oldTeam.id ) {
				this.teamLoaded = true;
			}
		},
		teamName() {
			this.sendTelemetry();
			// see DocumentMixin
			this.trySetDocumentTitle();
		},
		$route() {
			this.sendTelemetry();
		},
	},
	computed: {
		...mapGetters( "team-layout", [
			"activeGoalCount",
			"outstandingCommitmentCount",
			"team",
			"teamName",
		] ),
		// see: DocumentMixin
		// eslint-disable-next-line vue/no-unused-properties
		documentTitle() {
			const { teamName, } = this;
			if ( !teamName ) {
				return "";
			}
			return this.translate( "documentTitle", { name: teamName, } );
		},
		breadcrumbs() {
			const { team, } = this;
			return [
				makeCrumb( {
					text: this.translate( "breadcrumbText" ),
					route: "teams",
				} ),
				makeCrumb( {
					text: this.translate( "teamBreadcrumbText", team ),
					isActive: true,
				} ),
			];
		},
		tabs() {
			if ( !( this.userProfile?.personId && this.team?.id ) ) {
				return [];
			}

			const { navigation, } = this.$foundation();

			const route = navigation.getCurrentRoute().path;
			const isSettings = /settings/i.test( route );
			const isWorkstyle = /workstyle/i.test( route );
			const isGoals = /goals/i.test( route );
			const isCommitments = /commitments/i.test( route );
			const isTeamHealth = /health/i.test( route );
			const isStream = /stream/i.test( route );

			const tabs = [
				{
					id: "stream",
					label: this.translate( "streamTab" ),
					isActive: isStream,
				},
				{
					id: "workstyle",
					label: this.translate( "workstyleTab" ),
					isActive: isWorkstyle,
				},
				{
					id: "goals",
					label: this.translate( "goalsTab" ),
					isActive: isGoals,
					count: this.activeGoalCount,
				},
				{
					id: "commitments",
					label: this.translate( "commitmentsTab" ),
					isActive: isCommitments,
					count: this.outstandingCommitmentCount,
				},
				{
					id: "health",
					label: this.translate( "healthTab" ),
					isActive: isTeamHealth,
				},
				{
					id: "settings",
					label: this.translate( "settingsTab" ),
					isActive: isSettings,
				},
			];

			return compact( tabs );
		},
		showTitleSkeleton() {
			return !this.teamName;
		},
	},
	methods: {
		...mapMutations( "team-layout", [
			"applyTeamId",
		] ),
		...mapActions( "team-layout", [
			"tryFavoriteTeam",
			"tryUnfavoriteTeam",
		] ),
		/* @TODO: move to vuex action */
		sendTelemetry() {
			const { team, userProfile, } = this;
			if ( !team?.id ) {
				return;
			}

			const { telemetry, } = this.$foundation();

			const telemetryEvents = {
				commitments: telemetry.team.viewTeamCommitmentsTab,
				goals: telemetry.team.viewTeamGoalsTab,
				health: telemetry.team.viewTeamHealthTab,
				settings: telemetry.team.viewTeamSettingsTab,
				stream: telemetry.team.viewTeamStreamTab,
				workstyle: telemetry.team.viewTeamWorkStyleTab,
			};

			const { tabs, } = this;
			const activeTab = tabs.find( tab => tab.isActive );
			if ( !activeTab ) {
				return;
			}

			const telemetryEvent = telemetryEvents[ activeTab.id ];
			if ( !telemetryEvent ) {
				return;
			}

			const {
				id: teamId,
				name: teamName,
				members,
				workStyle: teamCompositeWorkStyleType,
			} = team;

			const { personId, } = userProfile;
			const thisMemberWorkStyleType = userProfile.assessments?.DISC?.moniker || "";
			const thisMember = members.find( member => member.personId === personId );
			const { role: thisMemberRole, } = thisMember;

			telemetryEvent( {
				teamId,
				teamName,
				teamCompositeWorkStyleType,
				thisMemberWorkStyleType,
				thisMemberRole,
			} );
		},
		fetchThisTeam() {
			const { ioTeam, ioGokr, ioOutcome, } = this.$foundation();
			ioTeam.fetchTeam( { teamId: this.teamId, refetch: true, } );
			ioGokr.fetchGoalMetadata();
			ioOutcome.fetchCommitmentMetadata( { teamId: this.teamId, } );
		},
		onStarClicked( e ) {
			const { teamId, } = this;
			if ( e.isToggled ) {
				this.tryFavoriteTeam( { teamId, } );
			} else {
				this.tryUnfavoriteTeam( { teamId, } );
			}
		},
		onBack() {
			const { navigation, } = this.$foundation();
			navigation.navigateToTeams();
		},
		onCrumb( { route, } ) {
			if ( route === "teams" ) {
				this.onBack();
			}
		},
		onNavItemClick( navId ) {
			const { navigation, } = this.$foundation();
			const idToNavMap = {
				stream: navigation.navigateToTeamSharedStream,
				workstyle: navigation.navigateToTeamWorkStyle,
				commitments: navigation.navigateToTeamCommitments,
				goals: navigation.team.gokr.navigateToGoals,
				settings: navigation.navigateToTeamSettings,
				health: navigation.navigateToTeamHealth,
			};
			const navigate = idToNavMap[ navId ];
			if ( !navigate ) {
				// eslint-disable-next-line no-console
				return console.error( `no navigation available for ID: ${ navId }` );
			}
			navigate( { teamId: this.teamId, } );
		},
	},
	created() {
		this.screen = new ScreenSizeDetector( {
			onWidthChange: () => {
				this.screenWidth = this.screen.width;
			},
		} );
		this.applyTeamId( this.teamId );
		this.fetchThisTeam();
		if ( this.teamId === this.team?.id ) {
			this.teamLoaded = true;
		}
	},
	mounted() {
		this.sendTelemetry();
	},
	unmounted() {
		this.screen.removeMainCallback( "widthchange" );
		this.screen = undefined;
	},
};
</script>

<style lang="scss">
#app {
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
	height: 100%;
}
</style>

<style lang="scss" scoped>
$team-layout-title-font-color: $color1-700;
$team-layout-unfaved-star-color: $color1-300;
$team-layout-nav-border-color: $color1-200;
$team-layout-menu-icon-color: $color2-500;
$team-layout-sample-chip-font-color: $color2-600;
$team-layout-sample-chip-bg-color: $color2-100;
$team-layout-listing-link-font-color: $color2-500;
$team-layout-listing-link-divider-color: $color1-400;

.team-layout__tab-container {
	@include auto-y-scroll();
	width: 100%;
	flex: 1;
	position: relative;
	display: flex;
	flex-direction: column;
}

.team-layout__title-layout {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: center;
	width: 100%;

	gap: 0.5rem;
}

.team-layout__fave-star {
	color: $team-layout-unfaved-star-color;
	cursor: pointer;
}

.team-layout__team-name {
	font-weight: bold;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;

	@include font-and-line-for(2);

	@include for-size('md') {
		max-width: unset;
	}
}

.team-layout__team-member-avatars {
	display: flex;
}

.team-layout__tab-nav-row {
	padding: 0 1rem 0 1rem;
	@include auto-x-scroll();
	overflow-y: hidden;
	border-bottom: .0625rem solid $team-layout-nav-border-color;
	text-align: left;
}

.team-layout__page-crumb-header {
	border-bottom: none;
}
</style>
